import React, { useState, useEffect, useCallback } from 'react';
import { Container, Row, Col, Card, Image, Alert, Button } from 'react-bootstrap';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import api from '../services/api';

export default function PublicProfile() {
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [isFollowing, setIsFollowing] = useState(false);
  const [followLoading, setFollowLoading] = useState(false);
  const { username } = useParams();
  const navigate = useNavigate();
  const { currentUser } = useAuth();

  // Base64 encoded default avatar
  const defaultAvatar = "data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMTIwIiBoZWlnaHQ9IjEyMCIgdmlld0JveD0iMCAwIDEyMCAxMjAiIGZpbGw9Im5vbmUiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+CjxyZWN0IHdpZHRoPSIxMjAiIGhlaWdodD0iMTIwIiByeD0iNjAiIGZpbGw9IiM0ZjllZmYiLz4KPHN2ZyB4PSIzMCIgeT0iMjAiIHdpZHRoPSI2MCIgaGVpZ2h0PSI4MCIgdmlld0JveD0iMCAwIDI0IDI0IiBmaWxsPSJ3aGl0ZSI+CjxwYXRoIGQ9Ik0xMiAxMmMzLjMxIDAgNi0yLjY5IDYtNnMtMi42OS02LTYtNi02IDIuNjktNiA2IDIuNjkgNiA2IDZ6bTAgMmMtNC4wMSAwLTEyIDIuMDEtMTIgNnYyaDE2di0yYzAtMy45OS03Ljk5LTYtMTItNnoiLz4KPC9zdmc+Cjwvc3ZnPgo=";

  const checkFollowStatus = useCallback(async (targetUsername) => {
    try {
      const { isFollowing } = await api.checkFollowing(targetUsername);
      setIsFollowing(isFollowing);
    } catch (error) {
      console.error('Error checking follow status:', error);
    }
  }, []);

  useEffect(() => {
    const loadUserProfile = async () => {
      try {
        setLoading(true);
        setError('');

        // Get user profile from API
        const { user } = await api.getUserProfile(username);
        setProfile(user);
        
        // Check if current user is following this profile
        if (currentUser && currentUser.username !== username) {
          await checkFollowStatus(username);
        }
        
      } catch (error) {
        console.error('Error loading profile:', error);
        
        if (error.message.includes('not found')) {
          setError('User not found');
        } else {
          setError('Failed to load profile: ' + error.message);
        }
      } finally {
        setLoading(false);
      }
    };

    if (username) {
      loadUserProfile();
    }
  }, [username, currentUser, checkFollowStatus]);

  const handleFollowToggle = async () => {
    if (!currentUser || !profile) return;

    try {
      setFollowLoading(true);
      
      await api.followUser(profile.username);
      
      // Toggle follow state
      const newIsFollowing = !isFollowing;
      setIsFollowing(newIsFollowing);
      
      // Update follower count in UI
      setProfile(prev => ({
        ...prev,
        followersCount: newIsFollowing 
          ? (prev.followersCount || 0) + 1 
          : (prev.followersCount || 0) - 1
      }));
    } catch (error) {
      console.error('Error toggling follow:', error);
      setError('Failed to update follow status');
    } finally {
      setFollowLoading(false);
    }
  };

  // Check if this is the current user's own profile
  const isOwnProfile = currentUser && profile && profile.username === currentUser.username;

  if (loading) {
    return (
      <div style={{ backgroundColor: '#0f1419', minHeight: '100vh' }} className="d-flex align-items-center justify-content-center">
        <div className="text-light">Loading profile...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ backgroundColor: '#0f1419', minHeight: '100vh' }}>
        <Container className="py-4">
          <Row className="justify-content-center">
            <Col lg={8}>
              <Alert variant="danger" className="text-center">
                <h4>Profile Not Found</h4>
                <p>{error}</p>
                {!currentUser && error.includes('logged in') ? (
                  <div className="d-flex gap-2 justify-content-center">
                    <Button variant="primary" onClick={() => navigate('/login')}>
                      <i className="fas fa-sign-in-alt me-2"></i>
                      Login
                    </Button>
                    <Button variant="outline-light" onClick={() => navigate('/signup')}>
                      <i className="fas fa-user-plus me-2"></i>
                      Sign Up
                    </Button>
                  </div>
                ) : (
                  <Button variant="primary" onClick={() => navigate('/dashboard')}>
                    <i className="fas fa-home me-2"></i>
                    Back to Dashboard
                  </Button>
                )}
              </Alert>
            </Col>
          </Row>
        </Container>
      </div>
    );
  }

  return (
    <div style={{ backgroundColor: '#0f1419', minHeight: '100vh' }}>
      <Container className="py-4">
        <Row className="justify-content-center">
          <Col lg={8}>
            <Card style={{ backgroundColor: '#1a1d29', border: 'none', borderRadius: '20px', overflow: 'hidden' }}>
              {/* Background Image Section */}
              <div 
                className="position-relative"
                style={{
                  height: '200px',
                  background: profile.backgroundImage 
                    ? `url(${api.getImageUrl(profile.backgroundImage)}) center/cover` 
                    : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                }}
              />

              <Card.Body className="text-center position-relative" style={{ marginTop: '-60px' }}>
                {/* Profile Picture */}
                <div className="position-relative d-inline-block mb-3">
                  <Image
                    src={profile.profilePicture ? api.getImageUrl(profile.profilePicture) : defaultAvatar}
                    roundedCircle
                    width="120"
                    height="120"
                    style={{ 
                      border: '4px solid #1a1d29',
                      objectFit: 'cover'
                    }}
                  />
                </div>

                <h2 className="text-light mb-1">{profile.displayName || profile.username}</h2>
                <p className="text-muted">@{profile.username}</p>
                
                {profile.bio && (
                  <p className="text-light mb-3" style={{ maxWidth: '400px', margin: '0 auto' }}>
                    {profile.bio}
                  </p>
                )}

                {profile.location && (
                  <p className="text-light mb-3">
                    <i className="fas fa-map-marker-alt me-2"></i>
                    {profile.location}
                  </p>
                )}

                <Row className="text-center mb-4">
                  <Col>
                    <h5 className="text-light mb-0">{profile.cardsCount || 0}</h5>
                    <small className="text-light">Cards</small>
                  </Col>
                  <Col>
                    <h5 className="text-light mb-0">{profile.followersCount || 0}</h5>
                    <small className="text-light">Followers</small>
                  </Col>
                  <Col>
                    <h5 className="text-light mb-0">{profile.followingCount || 0}</h5>
                    <small className="text-light">Following</small>
                  </Col>
                </Row>

                {!isOwnProfile && currentUser && (
                  <div className="d-flex gap-2 justify-content-center">
                    <Button
                      style={{ 
                        backgroundColor: isFollowing ? '#dc3545' : '#28a745', 
                        border: 'none' 
                      }}
                      onClick={handleFollowToggle}
                      disabled={followLoading}
                    >
                      <i className={`fas ${isFollowing ? 'fa-user-minus' : 'fa-user-plus'} me-2`}></i>
                      {followLoading ? 'Processing...' : (isFollowing ? 'Unfollow' : 'Follow')}
                    </Button>
                  </div>
                )}

                {!currentUser && (
                  <div className="d-flex gap-2 justify-content-center">
                    <Button
                      variant="outline-light"
                      onClick={() => navigate('/login')}
                    >
                      <i className="fas fa-sign-in-alt me-2"></i>
                      Login to Follow
                    </Button>
                  </div>
                )}

                {currentUser && isOwnProfile && (
                  <div className="d-flex gap-2 justify-content-center">
                    <Button
                      style={{ backgroundColor: '#4f9eff', border: 'none' }}
                      onClick={() => navigate('/profile')}
                    >
                      <i className="fas fa-edit me-2"></i>
                      Edit Profile
                    </Button>
                  </div>
                )}

                {profile.createdAt && (
                  <p className="text-light mt-3 small">
                    <i className="fas fa-calendar-alt me-2"></i>
                    Joined {new Date(profile.createdAt).toLocaleDateString('en-US', { 
                      year: 'numeric', 
                      month: 'long' 
                    })}
                  </p>
                )}
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </Container>
    </div>
  );
} 